import { sub } from 'date-fns';
import { computed, makeAutoObservable, observable } from 'mobx';

import LazyLoadingKeyValueStore, {
  addKeyAsId,
  addKeyAsIdAndDataSetters,
  EventBasedTransport,
  RemoteDataEvent,
} from '@codefluegel/lazy-loading-key-value-store';
import { mergeMap, from, of } from 'rxjs';
import {
  MaexchenCollectCredit,
  MaexchenRedeemCredit,
  RedeemCreditStatus,
  UserData,
} from '../types/transport';

export interface RedeemedPointsWithId extends MaexchenRedeemCredit {
  id: string;
  setStatus: (newStatus: RedeemCreditStatus) => Promise<void>;
}

export interface MaexchenCollectCreditWithId extends MaexchenCollectCredit {
  adminUserEmail?: string;
  id: string;
}

export interface OrderElement {
  id: string;
  timestamp: string;
  products: RedeemedPointsWithId[];
  status: RedeemCreditStatus;
}

const CREDITS_DURATION = {
  months: 24,
} as const;

export default class PointsStore {
  private collectPointsStore;

  private redeemedPointsStore;

  constructor(
    collectedTransport: EventBasedTransport<MaexchenCollectCredit>,
    private redeemedTransport: EventBasedTransport<MaexchenRedeemCredit>,
    getUserDataTransport: (uid: string) => Promise<UserData>,
  ) {
    makeAutoObservable<
      PointsStore,
      'collectPointsStore' | 'redeemedPointsStore'
    >(this, {
      collectPointsStore: observable,
      redeemedPointsStore: observable,
      allOrders: computed,
      groupPoints: computed,
      allEntries: computed,
      allCollectEntries: computed,
      allRedeemEntries: computed,
    });

    const remoteCollectDataEvent =
      collectedTransport.remoteDataEventObservable.pipe(
        mergeMap(dataEvent => {
          if (
            (dataEvent.type === 'entry_added' ||
              dataEvent.type === 'entry_updated') &&
            dataEvent.data.entry.addedExternally
          ) {
            return from(
              getUserDataTransport(dataEvent.data.entry.userId).then(
                userData =>
                  ({
                    type: dataEvent.type,
                    data: {
                      key: dataEvent.data.key,
                      entry: {
                        ...dataEvent.data.entry,
                        adminUserEmail: userData.email,
                      },
                    },
                  } as RemoteDataEvent<
                    MaexchenCollectCredit & { adminUserEmail?: string }
                  >),
              ),
            );
          }
          return of(dataEvent);
        }),
      );

    this.collectPointsStore = new LazyLoadingKeyValueStore(
      {
        ...collectedTransport,
        remoteDataEventObservable: remoteCollectDataEvent,
      },
      addKeyAsId(),
    );
    this.redeemedPointsStore = this.createRedeemedPointsStore();
  }

  private createRedeemedPointsStore() {
    return new LazyLoadingKeyValueStore(
      this.redeemedTransport,
      addKeyAsIdAndDataSetters('id', 'status'),
    );
  }

  private static sortByTimestamp<T extends { timestamp: number }>(
    unsorted: T[],
  ) {
    return [...unsorted].sort((a, b) => b.timestamp - a.timestamp);
  }

  get allCollectEntries(): (MaexchenCollectCredit & { id: string })[] {
    return this.collectPointsStore.entriesArray;
  }

  get allRedeemEntries(): RedeemedPointsWithId[] {
    return this.redeemedPointsStore.entriesArray;
  }

  get allOrders(): OrderElement[] {
    const groupedOrders = this.redeemedPointsStore.entriesArray.reduce(
      (
        acc: Record<
          number,
          { products: RedeemedPointsWithId[]; status: RedeemCreditStatus }
        >,
        product,
      ) => {
        const { timestamp, status } = product;
        if (acc[timestamp]) {
          acc[timestamp].products.push(product);
        } else {
          acc[timestamp] = { status, products: [product] };
        }
        return acc;
      },
      {},
    );

    // transform grouped orders into array
    const ordersArray = Object.entries(groupedOrders || {}).map(
      ([timestamp, { status, products }]) => ({
        id: timestamp,
        timestamp: new Date(parseInt(timestamp, 10)).toLocaleDateString(),
        status,
        products,
      }),
    );

    return ordersArray;
  }

  get groupPoints(): number {
    try {
      // changing timestamp does not trigger recalculation of computet,
      // but I think, we don't need it in such an exact way.
      const currentDate = new Date();

      const addedCredits: { credits: number; timestamp: number }[] =
        this.allCollectEntries
          .filter(entry => entry.credits > 0)
          .sort((a, b) => a.timestamp - b.timestamp);

      const negativeCollected = this.allCollectEntries
        .filter(entry => entry.credits < 0)
        .map(entry => ({
          timestamp: entry.timestamp,
          totalCredits: -entry.credits,
        }));

      const removedCredits = [
        ...this.allRedeemEntries,
        ...negativeCollected,
        // leads to remove of old entries:
        { timestamp: currentDate.valueOf(), totalCredits: 0 },
      ].sort((a, b) => a.timestamp - b.timestamp);

      const remainingCreditEvents = removedCredits.reduce(
        (remainingCredits, subtractEvent) =>
          PointsStore.invalidateCreditsForSubtractEvent(
            remainingCredits,
            subtractEvent.totalCredits,
            subtractEvent.timestamp,
          ),
        addedCredits,
      );

      return remainingCreditEvents.reduce(
        (sum, current) => sum + current.credits,
        0,
      );
    } catch (error) {
      console.warn('calculation error', error);
      return 0;
    }
  }

  get allEntries(): (MaexchenCollectCreditWithId | RedeemedPointsWithId)[] {
    return PointsStore.sortByTimestamp([
      ...this.allCollectEntries,
      ...this.allRedeemEntries,
    ]);
  }

  private static invalidateCreditsForSubtractEvent(
    collectEvents: { credits: number; timestamp: number }[],
    creditsToRemove: number,
    removeTimestamp: number,
  ) {
    const invalidBeforeTimestamp = sub(
      removeTimestamp,
      CREDITS_DURATION,
    ).valueOf();

    let remainingCreditsToRemove = creditsToRemove;
    const remainingEvents = collectEvents.flatMap(collectEvent => {
      if (collectEvent.timestamp <= invalidBeforeTimestamp) {
        return []; // will be filtered by flatMap
      }
      if (remainingCreditsToRemove === 0) {
        return collectEvent;
      }
      const creditsDiff = Math.min(
        collectEvent.credits,
        remainingCreditsToRemove,
      );
      remainingCreditsToRemove -= creditsDiff;
      if (collectEvent.credits <= creditsDiff) return [];
      return {
        timestamp: collectEvent.timestamp,
        credits: collectEvent.credits - creditsDiff,
      };
    });

    if (remainingCreditsToRemove > 0) {
      throw Error('more credits redeemed than available');
    }

    return remainingEvents;
  }
}
